import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Film,
  CreditCard,
  ScrollText,
  Settings as SettingsIcon,
  LogOut,
} from "lucide-react";
import { Logo } from "./Logo";
import { useSessionStore } from "../session";
import { useQuota, useUserMe } from "@/shared/api/queries";
import { useTranslation } from "@/shared/i18n/I18nProvider";

const NAV = [
  { to: "/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard },
  { to: "/products", labelKey: "nav.products", icon: Package },
  { to: "/projects", labelKey: "nav.clips", icon: Film },
  { to: "/settings/billing", labelKey: "nav.billing", icon: CreditCard, showPlanBadge: true },
  { to: "/settings/ai-activity", labelKey: "nav.aiActivity", icon: ScrollText },
  { to: "/settings", labelKey: "nav.settings", icon: SettingsIcon },
] as const;

/** Title-case the plan tier for the sidebar badge ("free" → "Free"). */
function planLabel(tier: string | undefined): string | null {
  if (!tier) return null;
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

export function Sidebar() {
  const { session, setSession } = useSessionStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const me = useUserMe();
  const quota = useQuota(session?.workspaceId);
  const tier = planLabel(quota.data?.plan_tier);
  return (
    <aside className="hidden h-dvh w-60 shrink-0 flex-col border-r border-default bg-elevated md:flex">
      <div className="flex h-16 items-center px-6">
        <Logo withWordmark={false} />
      </div>
      <nav className="flex-1 px-3">
        <ul className="space-y-1">
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                    isActive ? "bg-primary-soft text-primary" : "text-fg-muted"
                  }`
                }
              >
                <item.icon className="h-4 w-4" />
                <span className="flex-1">{t(item.labelKey)}</span>
                {/* Per Hi-Fi spec, the Plan nav row carries the user's
                    current tier as a small badge so they see "Plan · Free"
                    or "Plan · Pro" at a glance. Hidden until quota loads
                    to avoid layout shift. */}
                {"showPlanBadge" in item && item.showPlanBadge && tier && (
                  <span className="rounded-full bg-surface px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide text-fg-subtle">
                    {tier}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="border-t border-default p-4">
        <div className="mb-3 text-xs font-bold uppercase tracking-wider text-fg-subtle">
          {t("nav.workspace")}
        </div>
        <div className="flex items-center gap-2.5 rounded-lg bg-surface p-2.5 text-sm">
          {me.data?.avatar_url ? (
            <img
              src={me.data.avatar_url}
              alt={me.data.display_name ?? me.data.email}
              className="h-9 w-9 shrink-0 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-extrabold text-white">
              {(me.data?.display_name ?? me.data?.email ?? session?.userId ?? "U")
                .charAt(0)
                .toUpperCase()}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-bold text-fg">
              {me.data?.display_name ?? `User ${session?.userId?.slice(0, 6) ?? "—"}`}
            </div>
            <div className="truncate text-xs text-fg-subtle">
              {me.data?.email ?? `ws ${session?.workspaceId?.slice(0, 8) ?? "—"}`}
            </div>
          </div>
        </div>
        <button
          onClick={() => {
            setSession(null);
            navigate("/sign-in");
          }}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg border border-default px-3 py-2 text-xs font-semibold text-fg-muted"
        >
          <LogOut className="h-3.5 w-3.5" /> {t("nav.signOut")}
        </button>
      </div>
    </aside>
  );
}
